import { Auth } from "@aws-amplify/auth"
import { useCallback, useState } from "react"

/**
 *
 * @param url
 * @param method
 * @returns
 */
const useLazyQuery = <InputBody, OutputBody>(
	url: string,
	method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE" = "GET",
	allow504 = false,
) => {
	const [data, setData] = useState<OutputBody | undefined>(undefined)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | undefined>(undefined)
	// wrap the query in useCallback so the function doesn't change and trigger other useEffect hooks
	const rawQuery = async (parameters: Parameters<InputBody> = {}): Promise<OutputBody> => {
		// parameters url takes priority over initial url
		parameters.url = parameters.url || url
		if (parameters.queryParameters) {
			parameters.url += "?" + parameters.queryParameters.join("&")
		}
		parameters.method = parameters.method || method
		setLoading(true)
		let response: Response
		try {
			// get the Cognito auth token that allows requests to ApiGateway
			const session = await Auth.currentSession()
			const cognitoToken = session.getIdToken().getJwtToken()
			// send API request
			response = await fetch(parameters.url, {
				method: parameters.method,
				headers: {
					Authorization: cognitoToken,
				},
				body: parameters.body && JSON.stringify(parameters.body),
			})
		} catch (e) {
			console.error("Error in fetch command", e)
			// leave data alone in case this is a refresh
			setLoading(false)
			setError("System Error")
			throw e
		}
		if (response.status === 200 || response.status === 201) {
			// handle successful response
			const body = await response.text()
			const output: OutputBody = body && JSON.parse(body)
			setData(output)
			setLoading(false)
			setError(undefined)
			return output
		}
		//this is a hack to allow us to ignore 504 errors for the recalculate-keycard-doors endpoint
		if (allow504 && response.status === 504) {
			// handle successful response
			const body = await response.text()
			const output: OutputBody = body && JSON.parse(body)
			setData(output)
			setLoading(false)
			setError(undefined)
			return output
		}
		// handle error responses
		// TODO remove?
		if (response.status === 401) {
			console.log("Auth error")
			console.log(response)
		}
		const body = await response.text()
		const json = body && JSON.parse(body)
		setData(undefined)
		setLoading(false)
		setError(json?.userError || json?.message || response.statusText)
		throw Error(json?.developerError || response)
	}
	const query = useCallback(rawQuery, [url, method, allow504])
	return { query, data, loading, error }
}
export default useLazyQuery

export interface Parameters<InputBody> {
	url?: string
	queryParameters?: string[]
	method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
	body?: InputBody
}
